import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  Col,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Input,
  FormGroup
} from "reactstrap";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import PageHeader from "../common/PageHeader";
import FalconCardHeader from "../common/FalconCardHeader";
import withRedirect from "../../helpers/hoc/withRedirect";
import ButtonIcon from "../common/ButtonIcon";
import {
  faFileExcel,
  faFilter,
  faInfoCircle,
  faPlus,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { useParams } from "react-router";
import useSWRFetchPagination from "../../helpers/hooks/useSWRFetchPagination";
import {
  fetchAliquots,
  exportAliquotsToExcel,
  importPropertiesAliquotFromExcel,
  fetchSingleSpecialAliquotProfile,
  saveSingleSpecialAliquotProfile
} from "../../helpers/api/finance";
import { isIterableArray } from "../../helpers/utils";
import FullPagination from "../common/pagination/FullPagination";
import { useSelector } from "react-redux";
import AliquotListItem from "../common/AliquotListItem";
import { Toast } from "./../UI/toast/Toast";
import { Link } from "react-router-dom";
import Datetime from "react-datetime";
import { Message, Or, PreloadState } from "../UI/preloading/PreloadState";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SupportLink from "../root/support/components/SupportLink";
import FalconDropzone from "../common/FalconDropzone";
import DownloadFormatDropdown from "../common/DownloadFormatDropdown";
import OnboardingBanner from "../onboarding/OnboardingBanner";
import AliquotHealthAlert from "../onboarding/AliquotHealthAlert";
import useOnboardingProgress from "../../helpers/hooks/useOnboardingProgress";
import useModuleBase from "../../helpers/hooks/useModuleBase";

const Aliquots = ({ setRedirect, setRedirectUrl, showPagination = true }) => {
  const moduleBase = useModuleBase();

  const { id } = useParams();
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const [loading, setLoading] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState([]);
  const [exporting, setExporting] = useState(false);

  // Special Aliquot State
  const [specialAliquots, setSpecialAliquots] = useState({});
  const [editSpecialModalOpen, setEditSpecialModalOpen] = useState(false);
  const [selectedAliquotForSpecial, setSelectedAliquotForSpecial] = useState(null);
  const [specialPercentageInput, setSpecialPercentageInput] = useState("");

  const [params, setParams] = useState({
    state: 0,
    condominiumId: id,
    start_date: null,
    end_date: null,
  });
  const [formDate, setformDate] = useState({
    since: null,
    to: null,
  });
  const {
    data,
    error,
    isLoading,
    mutate,
    meta,
    handler: { nextPage, previousPage, perPage, goToPage },
  } = useSWRFetchPagination(fetchAliquots, params);

  // HO-HU-36: el estado de alicuotas del condominio (cuantas unidades quedaron
  // sin alicuota, cuanto suman) viene del endpoint de la guia de primeros
  // pasos, que ya lo calcula. Se muestra aca aunque el manager haya ocultado la
  // guia: no es un paso del onboarding, es un problema de datos que deja
  // unidades fuera del cobro.
  const { aliquot: aliquotHealth, refresh: refreshOnboarding } =
    useOnboardingProgress();

  useEffect(() => {
    const loadSpecialProfile = async () => {
      try {
        const response = await fetchSingleSpecialAliquotProfile(id);
        if (response && response.data && response.data.aliquots) {
          setSpecialAliquots(response.data.aliquots || {});
        }
      } catch (error) {
        console.error("Error loading special aliquot profile", error);
      }
    };
    if (id) {
      loadSpecialProfile();
    }
  }, [id]);

  const toggleEditSpecialModal = () => {
    setEditSpecialModalOpen(!editSpecialModalOpen);
    setSpecialPercentageInput("");
    if (!editSpecialModalOpen) {
      setSelectedAliquotForSpecial(null);
    }
  };

  const handleEditSpecial = (aliquot) => {
    setSelectedAliquotForSpecial(aliquot);
    const propId = aliquot.property?.id;
    if (propId) {
      setSpecialPercentageInput(specialAliquots[propId] ?? "");
    }
    setEditSpecialModalOpen(true);
  };

  const handleSaveSpecialAliquot = async () => {
    if (!selectedAliquotForSpecial) return;
    const propId = selectedAliquotForSpecial.property?.id;
    if (!propId) return;

    const val = parseFloat(specialPercentageInput);
    const newSpecialAliquots = { ...specialAliquots };

    if (isNaN(val) || specialPercentageInput === "") {
      delete newSpecialAliquots[propId];
    } else {
      newSpecialAliquots[propId] = val;
    }

    setSpecialAliquots(newSpecialAliquots);

    try {

      const payload = {
        condominium_id: id,
        aliquots: newSpecialAliquots,

      };

      const currentProfile = await fetchSingleSpecialAliquotProfile(id);
      let currentProps = [];
      if (currentProfile && currentProfile.data) {
        currentProps = currentProfile.data.properties || [];
      }

      payload.properties = currentProps;

      await saveSingleSpecialAliquotProfile(payload);

      Toast.fire({
        type: "success",
        title: "Alícuota especial actualizada",
      });
      toggleEditSpecialModal();

    } catch (error) {
      console.error(error);
      Toast.fire({
        type: "error",
        title: "Error al guardar",
      });
    }
  };

  const ButtonAdd = ({ onClick }) => {
    return (
      <ButtonIcon color="falcon-default" onClick={onClick} icon={faPlus}>
        Añadir alícuota
      </ButtonIcon>
    );
  };

  const handleCreateAliquot = () => {
    setRedirectUrl(`/dashboard/${moduleBase}/${id}/aliquots/create`);
    setRedirect(true);
  };

  /**
   * Baja las alícuotas del condominio tal como están hoy, en el formato que se
   * elija.
   *
   * Antes bajaba una plantilla vacía: una sola columna de números, sin nada
   * que dijera a qué unidad correspondía cada uno, así que la carga los
   * aplicaba por orden de fila. Con el archivo actual cada fila trae su
   * usuario y su inmueble, y el administrador corrige sobre lo que ya hay.
   */
  const downloadCurrentAliquots = async (format = 'xlsx') => {
    setExporting(true);
    try {
      const blob = await exportAliquotsToExcel(selectedCondominium, format);
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.setAttribute('download', `alicuotas.${format}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } catch (error) {
      Toast.fire({
        type: 'error',
        title:
          error?.data?.error ||
          error?.data?.message ||
          'No se pudo descargar el archivo de alícuotas.',
        timer: 5000,
      });
    } finally {
      setExporting(false);
    }
  };


  const toggleImportModal = () => {
    setImportModalOpen(!importModalOpen);
    setImportFile([]);
  };

  const handleImport = async () => {
    if (importFile.length === 0) {
      Toast.fire({
        type: "error",
        title: "Por favor seleccione un archivo.",
      });
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", importFile[0].file);

      Toast.fire({
        type: "info",
        title: "Cargando archivo...",
      });

      const response = await importPropertiesAliquotFromExcel(
        selectedCondominium,
        formData
      );

      Toast.fire({
        type: "success",
        title: response.message || "Alícuotas importadas exitosamente.",
      });
      if (response?.warning) {
        Toast.fire({
          type: "warning",
          title: response.warning,
          timer: 8000,
        });
      }

      toggleImportModal();
      setParams((prevParams) => ({ ...prevParams }));
    } catch (error) {
      const rowErrors = Array.isArray(error?.data?.detalles) ? error.data.detalles : error?.data?.errors;
      if (Array.isArray(rowErrors) && rowErrors.length > 0) {
        const errorList = rowErrors.join('\n');
        Toast.fire({
          type: "error",
          title: Array.isArray(error?.data?.detalles) ? "Errores en la importación" : error.data.message || "Errores en la importación",
          html: `<pre style="text-align: left; font-size: 12px; max-height: 300px; overflow-y: auto;">${errorList}</pre>`,
          timer: 10000, // 10 segundos para leer los errores
          timerProgressBar: true,
        });
      } else {
        Toast.fire({
          type: "error",
          title: error?.data?.error || error?.data?.message || "Error al importar alícuotas.",
          timer: 5000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (aliquotId) => {
    setRedirectUrl(`/dashboard/${moduleBase}/${id}/aliquots/edit/${aliquotId}`);
    setRedirect(true);
  };

  // HO-ISSUE-116: una alícuota no se puede eliminar. El motivo está en
  // AliquotListItem, que es donde vivía la opción.

  const renderItems = () => {
    return data.map((aliquot, i) => {
      const propId = aliquot.property?.id;
      return (
        <AliquotListItem
          key={aliquot.id}
          amount={aliquot.value}
          name={`${aliquot?.property?.user?.data?.firstname || ""} ${aliquot?.property?.user?.data?.lastname || ""
            }`}
          handleEdit={() => handleEdit(aliquot.id)}
          createdAt={aliquot.created_at}
          options={true}
          property={aliquot.property?.property}
          specialAmount={propId ? specialAliquots[propId] : null}
          onEditSpecial={() => handleEditSpecial(aliquot)}
        />
      );
    });
  };

  return (
    <>
      <PageHeader
        title="Alícuotas"
        titleTag="h2"
        description="Gestiona y administra cada una de tus finanzas."
        className="mb-3"
        col={{ lg: 9 }}
      >
        <SupportLink />
      </PageHeader>
      <OnboardingBanner steps={["aliquot"]} />
      <AliquotHealthAlert
        aliquot={aliquotHealth}
        className="shadow-sm mb-3"
        onDistributed={async () => {
          await Promise.all([mutate(), refreshOnboarding()]);
        }}
      />
      <Card>
        <FalconCardHeader title="Alícuotas">
          <Row className="justify-content-end">
            <Col xs="auto" xl={12} lg={12} md={12} sm={12}>
              <Row className="justify-content-between align-items-center">
                <Col
                  xs="auto"
                  lg="auto"
                  md={12}
                  sm={12}
                  className="d-flex align-items-center justify-content-center"
                >
                  <Label className="mb-0 mr-2">
                    <FontAwesomeIcon icon={faFilter} />
                  </Label>
                </Col>
                <Col
                  xs="auto"
                  sm="auto"
                  lg="auto"
                  className="d-flex align-items-center flex-sm-column flex-md-column flex-lg-row"
                >
                  <Label className="mr-2 mb-0">Desde</Label>
                  <Datetime
                    value={formDate.since}
                    isValidDate={(current, selected) => {
                      return !current.isAfter();
                    }}
                    onChange={(d) =>
                      setformDate((prev) => ({ ...prev, since: d }))
                    }
                    dateFormat="DD/MM/YYYY"
                    timeFormat={false}
                  />
                </Col>
                <Col
                  xs="auto"
                  sm="auto"
                  lg="auto"
                  className="d-flex align-items-center flex-sm-column flex-md-column flex-lg-row"
                >
                  <Label className="mr-2 mb-0">Hasta</Label>
                  <Datetime
                    value={formDate.to}
                    className="mr-2"
                    isValidDate={(current, selected) => {
                      return (
                        current.isAfter(formDate.since) && !current.isAfter()
                      );
                    }}
                    dateFormat="DD/MM/YYYY"
                    timeFormat={false}
                    onChange={(d) =>
                      setformDate((prev) => ({ ...prev, to: d }))
                    }
                  />
                </Col>
                <Col
                  xs="auto"
                  className="pl-0 d-flex align-items-md-end align-items-lg-center align-items-sm-end"
                >
                  <ButtonIcon
                    className="text-nowrap btn-block"
                    disabled={!formDate.to}
                    icon={faSearch}
                    color="falcon-default"
                    onClick={() => {
                      setParams((prev) => {
                        return {
                          ...prev,
                          start_date: formDate.since.format("YYYY-MM-DD"),
                          end_date: formDate.to.format("YYYY-MM-DD"),
                        };
                      });
                    }}
                  >
                    Buscar
                  </ButtonIcon>
                </Col>
              </Row>
              <hr />
              <Row className="justify-content-end align-items-center">
                <Col
                  xs={12}
                  md={"auto"}
                  className="text-center text-md-right my-1"
                >
                  {/*    <ButtonIcon
                    className="mb-2 mb-md-0"
                    icon={faDownload}
                    color="falcon-default"
                    onClick={downloadTemplate}
                  ></ButtonIcon> */}

                  <ButtonIcon
                    className="ml-2 mb-2 mb-md-0"
                    icon={faFileExcel}
                    color="falcon-default"
                    onClick={toggleImportModal}
                    disabled={loading}
                  >
                    {loading ? "Cargando..." : "Cargar archivo"}
                  </ButtonIcon>
                </Col>

              </Row>
            </Col>
            {/*   <Col xs='auto' lg='auto' md={12} sm={12} className='mt-3'>
                        <ButtonAdd onClick={handleCreateAliquot} />
                    </Col> */}
          </Row>
        </FalconCardHeader>
        <CardBody className={isIterableArray(data) ? "p-0" : ""}>
          <PreloadState cond={isIterableArray(data)} loading={isLoading}>
            <Or value={true}>
              <ul className="list-unstyled mb-0">{renderItems()}</ul>
            </Or>
            <Or value={false} cond={!!error}>
              <Message
                value={false}
                color={"info"}
                className="p-3"
                align="left"
                icon={faInfoCircle}
              >
                <>
                  Aún no hay alícuotas para mostrar, Puedes crear uno ahora
                  mismo pulsando el siguiente enlace
                  <Button
                    data-testid="Button"
                    tag={Link}
                    color="link"
                    size="sm"
                    className="ml-1 pl-0"
                    to={`/dashboard/${moduleBase}/${selectedCondominium}/aliquots/create`}
                  >
                    {"Crear alícuota."}
                  </Button>
                </>
              </Message>
              <Or value={true}>
                <Alert color="danger" className="mb-0">
                  Esta página no está disponible
                  <Button
                    data-testid="Button"
                    color="link"
                    size="sm"
                    className="ml-1 pl-0"
                    onClick={() => {
                      setRedirectUrl(`/dashboard/banks`);
                      setRedirect(true);
                    }}
                  >
                    Volver
                  </Button>
                </Alert>
              </Or>
            </Or>
          </PreloadState>
        </CardBody>
        {isIterableArray(data) && (
          <CardFooter className="py-2 px-3">
            <FullPagination
              nextPage={nextPage}
              prevPage={previousPage}
              goToPage={goToPage}
              meta={meta}
              className={"shadow-none"}
              posText={"cuentas"}
              perPage={perPage}
            />
          </CardFooter>
        )}
      </Card>
      <Modal isOpen={importModalOpen} toggle={toggleImportModal}>
        <ModalHeader toggle={toggleImportModal}>Importar Alícuotas</ModalHeader>
        <ModalBody>
          <div className="d-flex justify-content-between mb-3">
            <p className="fs--1 font-weight-semi-bold mb-0">
              Descarga el archivo con las alícuotas como están hoy, corrígelo y
              vuelve a subirlo:
            </p>
            <DownloadFormatDropdown
              label={
                exporting ? "Descargando..." : "Descargar Alícuotas Actuales"
              }
              disabled={exporting}
              onSelect={downloadCurrentAliquots}
            />
          </div>
          <FalconDropzone
            files={importFile}
            onChange={(files) => {
              setImportFile(files);
            }}
            multiple={false}
            preview
            accept=".xlsx, .xls, .csv, .txt"
            placeholder={
              <>
                <div className="fs-2 mb-2">
                  <FontAwesomeIcon icon={faFileExcel} />
                </div>
                <p className="fs--1 mb-0">Arrastre su archivo aquí</p>
              </>
            }
          />
          <div className="mt-3">
            <p className="fs--1 text-muted mb-0 font-weight-semi-bold">
              Detalles de la importación:
            </p>
            <p className="fs--1 text-muted mb-0">
              No modifiques las columnas <strong>Usuario</strong> e{" "}
              <strong>Inmueble</strong>: son las que identifican cada unidad. Lo
              que se edita es <strong>Alicuota</strong>.
            </p>
            <p className="fs--1 text-muted mb-0">
              El archivo tiene que traer todas las unidades, con su alícuota en
              puntos porcentuales (5.0000 para 5%, no 0.05). Se guardan tal cual:
              si por el documento del condominio no suman 100%, no hace falta
              ajustarlas. Si falta una unidad o un valor no es válido, no se carga
              nada y se te indica qué corregir.
            </p>
            {/* HO-TASK-249 */}
            <p className="fs--1 text-muted mb-0">
              Formatos aceptados: Excel (.xlsx, .xls) y texto (.csv, .txt).
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleImportModal}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleImport} disabled={importFile.length === 0 || loading}>
            {loading ? "Importando..." : "Importar"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Special Aliquot Modal */}
      <Modal isOpen={editSpecialModalOpen} toggle={toggleEditSpecialModal}>
        <ModalHeader toggle={toggleEditSpecialModal}>
          {selectedAliquotForSpecial && specialAliquots[selectedAliquotForSpecial.property?.id] ? "Editar Alícuota Especial" : "Asignar Alícuota Especial"}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Propiedad</Label>
            <Input disabled value={selectedAliquotForSpecial ? `${selectedAliquotForSpecial.property?.user?.data?.firstname} ${selectedAliquotForSpecial.property?.user?.data?.lastname}` : ""} />
          </FormGroup>
          <FormGroup>
            <Label>Porcentaje de Alícuota Especial (%)</Label>
            <Input
              type="number"
              step="0.0001"
              value={specialPercentageInput}
              onChange={(e) => setSpecialPercentageInput(e.target.value)}
              placeholder="Ej: 15.5000"
              autoFocus
            />
            <small className="text-muted">Deje en blanco para eliminar la alícuota especial de esta propiedad.</small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleEditSpecialModal}>Cancelar</Button>
          <Button color="primary" onClick={handleSaveSpecialAliquot}>Guardar</Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

Aliquots.propTypes = {
  showPagination: PropTypes.bool,
};

export default withRedirect(Aliquots);
